// src/components/DocumentScanModal.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  fileToDataURL,
  dataURLToImage,
  renderAndCompress,
} from "../utils/imageTools";

export default function DocumentScanModal({
  show,
  file,
  title = "Document Scan",
  options = {},
  onCancel,
  onConfirm,
}) {
  const [sourceURL, setSourceURL] = useState("");
  const [imgEl, setImgEl] = useState(null);
  const [rotation, setRotation] = useState(0);
  const [zoom, setZoom] = useState(1);
  const [crop, setCrop] = useState({ top: 0, right: 0, bottom: 0, left: 0 });
  const [busy, setBusy] = useState(false);
  const canvasRef = useRef(null);

  // Load file into Image/DataURL
  useEffect(() => {
    let canceled = false;
    (async () => {
      if (!file || !show) return;
      try {
        const url = await fileToDataURL(file);
        if (canceled) return;
        setSourceURL(url);
        const img = await dataURLToImage(url);
        if (!canceled) setImgEl(img);
      } catch (e) {
        console.error("DocumentScan load failed:", e);
      }
    })();
    return () => {
      canceled = true;
      setImgEl(null);
      setSourceURL("");
    };
  }, [file, show]);

  // Draw the current preview (low-cost render for UI)
  useEffect(() => {
    if (!imgEl || !canvasRef.current) return;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");

    // A small preview canvas (not full resolution)
    const PREVIEW_W = 480;
    const PREVIEW_H = 360;
    canvas.width = PREVIEW_W;
    canvas.height = PREVIEW_H;

    // Render onto an offscreen large canvas with current rotation/zoom
    const radians = (rotation * Math.PI) / 180;
    const sin = Math.abs(Math.sin(radians));
    const cos = Math.abs(Math.cos(radians));
    const w = imgEl.width;
    const h = imgEl.height;

    // rotated bounding box
    const bbW = w * cos + h * sin;
    const bbH = w * sin + h * cos;

    const off = document.createElement("canvas");
    off.width = bbW;
    off.height = bbH;
    const octx = off.getContext("2d");
    octx.translate(bbW / 2, bbH / 2);
    octx.rotate(radians);
    octx.scale(zoom, zoom);
    octx.drawImage(imgEl, -w / 2, -h / 2);

    // Crop edges (percentages)
    const leftPx = off.width * (Math.max(0, Math.min(40, crop.left)) / 100);
    const rightPx = off.width * (Math.max(0, Math.min(40, crop.right)) / 100);
    const topPx = off.height * (Math.max(0, Math.min(40, crop.top)) / 100);
    const bottomPx =
      off.height * (Math.max(0, Math.min(40, crop.bottom)) / 100);

    const cropX = Math.floor(leftPx);
    const cropY = Math.floor(topPx);
    const cropW = Math.max(1, Math.floor(off.width - leftPx - rightPx));
    const cropH = Math.max(1, Math.floor(off.height - topPx - bottomPx));

    // Fit cropped area into preview canvas
    ctx.clearRect(0, 0, PREVIEW_W, PREVIEW_H);
    const scale = Math.min(PREVIEW_W / cropW, PREVIEW_H / cropH);
    const viewW = cropW * scale;
    const viewH = cropH * scale;
    const x = (PREVIEW_W - viewW) / 2;
    const y = (PREVIEW_H - viewH) / 2;
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = "medium";
    ctx.drawImage(off, cropX, cropY, cropW, cropH, x, y, viewW, viewH);
  }, [imgEl, rotation, zoom, crop]);

  const rotateLeft = () => setRotation((r) => (r - 90 + 360) % 360);
  const rotateRight = () => setRotation((r) => (r + 90) % 360);

  const onUseThis = async () => {
    if (!imgEl) return;
    setBusy(true);
    try {
      const blob = await renderAndCompress({
        image: imgEl,
        rotationDeg: rotation,
        zoom,
        crop,
        // Defaults for ID scans; caller can override via options prop
        maxWidth: options.maxWidth ?? 1600,
        maxHeight: options.maxHeight ?? 1600,
        maxBytes: options.maxBytes ?? 500 * 1024,
        mimeType: options.mimeType ?? "image/jpeg",
        initialQuality: options.initialQuality ?? 0.85,
        minQuality: options.minQuality ?? 0.6,
      });
      onConfirm && onConfirm(blob);
    } catch (e) {
      console.error(e);
      alert("Failed to process image. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  if (!show) return null;

  return (
    <div
      className="modal d-block"
      tabIndex="-1"
      role="dialog"
      aria-modal="true"
    >
      <div className="modal-dialog modal-xl">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">{title}</h5>
            <button
              className="btn-close"
              onClick={onCancel}
              aria-label="Close"
            />
          </div>
          <div className="modal-body">
            {!imgEl ? (
              <p className="text-muted mb-0">Loading preview…</p>
            ) : (
              <>
                <div className="row g-3">
                  <div className="col-12 col-lg-7">
                    <div className="border rounded p-2 text-center bg-light">
                      <canvas ref={canvasRef} style={{ maxWidth: "100%" }} />
                    </div>
                  </div>
                  <div className="col-12 col-lg-5">
                    <div className="d-flex gap-2 mb-3">
                      <button
                        className="btn btn-outline-secondary"
                        type="button"
                        onClick={rotateLeft}
                      >
                        <i className="bi bi-arrow-counterclockwise" /> Rotate
                        -90°
                      </button>
                      <button
                        className="btn btn-outline-secondary"
                        type="button"
                        onClick={rotateRight}
                      >
                        <i className="bi bi-arrow-clockwise" /> Rotate +90°
                      </button>
                    </div>

                    <div className="mb-3">
                      <label className="form-label">Zoom</label>
                      <input
                        type="range"
                        className="form-range"
                        min="1"
                        max="3"
                        step="0.05"
                        value={zoom}
                        onChange={(e) => setZoom(Number(e.target.value))}
                      />
                      <div className="small text-muted">
                        Use zoom to fill the frame (1–3x).
                      </div>
                    </div>

                    <div className="mb-2">
                      <strong>Crop edges (%)</strong>
                    </div>
                    <div className="mb-2">
                      <label className="form-label mb-1">Top</label>
                      <input
                        type="range"
                        className="form-range"
                        min="0"
                        max="40"
                        step="1"
                        value={crop.top}
                        onChange={(e) =>
                          setCrop((c) => ({
                            ...c,
                            top: Number(e.target.value),
                          }))
                        }
                      />
                    </div>
                    <div className="mb-2">
                      <label className="form-label mb-1">Right</label>
                      <input
                        type="range"
                        className="form-range"
                        min="0"
                        max="40"
                        step="1"
                        value={crop.right}
                        onChange={(e) =>
                          setCrop((c) => ({
                            ...c,
                            right: Number(e.target.value),
                          }))
                        }
                      />
                    </div>
                    <div className="mb-2">
                      <label className="form-label mb-1">Bottom</label>
                      <input
                        type="range"
                        className="form-range"
                        min="0"
                        max="40"
                        step="1"
                        value={crop.bottom}
                        onChange={(e) =>
                          setCrop((c) => ({
                            ...c,
                            bottom: Number(e.target.value),
                          }))
                        }
                      />
                    </div>
                    <div className="mb-2">
                      <label className="form-label mb-1">Left</label>
                      <input
                        type="range"
                        className="form-range"
                        min="0"
                        max="40"
                        step="1"
                        value={crop.left}
                        onChange={(e) =>
                          setCrop((c) => ({
                            ...c,
                            left: Number(e.target.value),
                          }))
                        }
                      />
                    </div>

                    <div className="alert alert-info mt-3">
                      <div className="small">
                        Final image will be auto‑compressed before upload.
                      </div>
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
          <div className="modal-footer">
            <button
              className="btn btn-outline-secondary"
              onClick={onCancel}
              disabled={busy}
            >
              Cancel
            </button>
            <button
              className="btn btn-primary"
              onClick={onUseThis}
              disabled={!imgEl || busy}
            >
              {busy ? "Processing…" : "Use this"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
